import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';

import type { CarLocation } from '@/types/CarLocation';

const StyledStack = styled(Stack)(({ theme }) => ({
  height: '3rem',
  justifyContent: 'center',
  color: theme.palette.text.secondary,

  '.kmx-car-tile--unavailable &': {
    color: theme.palette.text.disabled,
  },
}));

type LocationProps = {
  locationInfo: CarLocation;
  distance: number;
};

const Location = ({ locationInfo, distance }: LocationProps) => {
  const {
    storeName,
    stateAbbreviation,
    transferText,
    transferTimesUnavailable,
    minEstimatedTransferDurationInDays,
    maxEstimatedTransferDurationInDays,
  } = locationInfo;

  // Transfer eta only applies when distance > 50 miles.
  const transferTimeMin = useMemo(
    () => (distance <= 50 ? 0 : minEstimatedTransferDurationInDays),
    [distance, minEstimatedTransferDurationInDays],
  );
  const transferTimeMax = useMemo(
    () => (distance <= 50 ? 0 : maxEstimatedTransferDurationInDays),
    [distance, maxEstimatedTransferDurationInDays],
  );

  const addDays = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  };

  const getTransferTime = () => {
    if (transferTimesUnavailable) {
      return '| Est. arrival unavailable';
    }

    if (!transferTimeMin || !transferTimeMax) {
      return '';
    }

    const minDate = addDays(transferTimeMin);
    const maxDate = addDays(transferTimeMax);

    //Add 1 to the date.getMonth() since it’s 0-based indexing
    const startDate = `${minDate.getMonth() + 1}/${minDate.getDate()}`;
    const endDate = `${maxDate.getMonth() + 1}/${maxDate.getDate()}`;

    return `| Est. arrival ${startDate}-${endDate}`;
  };

  const transferTimeText = getTransferTime();

  return (
    <StyledStack className="kmx-car-tile__location-info">
      <Typography variant="caption" color="inherit">
        {transferText} {transferTimeText}
      </Typography>
      {storeName && stateAbbreviation && (
        <Typography variant="caption" color="inherit">
          CarMax {storeName}, {stateAbbreviation}
        </Typography>
      )}
    </StyledStack>
  );
};

export default Location;
